import { useEffect, useState } from 'react';
import {
  NOTIFICATION_TYPES,
  ensureStudentPushRegistration,
  getPushPermissionState,
  getStudentNotificationPreferences,
  updateStudentNotificationPreferences
} from '../../../utils/studentPushNotifications';

function Toggle({ checked, disabled, onChange }) {
  return (
    <label style={styles.switch}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={event => onChange(event.target.checked)}
        style={styles.hiddenCheckbox}
      />
      <span
        style={{
          ...styles.track,
          ...(checked ? styles.trackOn : {}),
          ...(disabled ? styles.trackDisabled : {})
        }}
      >
        <span
          style={{
            ...styles.thumb,
            ...(checked ? styles.thumbOn : {})
          }}
        />
      </span>
    </label>
  );
}

function permissionLabel(permission) {
  if (permission === 'granted') return 'Enabled on this device';
  if (permission === 'denied') return 'Blocked in Android settings';
  if (permission === 'prompt') return 'Permission not requested yet';
  if (permission === 'prompt-with-rationale') return 'Permission required';
  if (permission === 'unsupported') return 'Not available on this device';
  return 'Checking device permission...';
}

export default function MobileNotificationSettings({
  session,
  onBack
}) {
  const [preferences, setPreferences] = useState(null);
  const [permission, setPermission] = useState('checking');
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError('');

      try {
        const [prefs, devicePermission] = await Promise.all([
          getStudentNotificationPreferences(session.studentId),
          getPushPermissionState()
        ]);

        if (!cancelled) {
          setPreferences(prefs);
          setPermission(devicePermission);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.message ||
            'Notification settings could not be loaded.'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [session.studentId]);

  async function save(values, key) {
    setSavingKey(key);
    setError('');

    try {
      const next = await updateStudentNotificationPreferences(
        session.studentId,
        values
      );

      setPreferences(next);
      return next;
    } catch (err) {
      setError(
        err?.message ||
        'Notification settings could not be saved.'
      );
      return null;
    } finally {
      setSavingKey('');
    }
  }

  async function changeMaster(enabled) {
    if (enabled) {
      setSavingKey('push_enabled');
      setError('');

      try {
        const result = await ensureStudentPushRegistration(
          session,
          {
            askPermission: true,
            ignoreStoredPreference: true
          }
        );

        setPermission(result.permission);

        if (result.permission !== 'granted') {
          setError(
            result.permission === 'denied'
              ? 'Android is blocking notifications for this app. Enable them in the app’s Android notification settings.'
              : 'Android notification permission was not granted.'
          );
          setSavingKey('');
          return;
        }

        const next = await updateStudentNotificationPreferences(
          session.studentId,
          { push_enabled: true }
        );
        setPreferences(next);
      } catch (err) {
        setError(
          err?.message ||
          'Push notifications could not be enabled.'
        );
      } finally {
        setSavingKey('');
      }

      return;
    }

    await save({ push_enabled: false }, 'push_enabled');
  }

  async function changeType(key, enabled) {
    await save({ [key]: enabled }, key);
  }

  return (
    <>
      <div style={styles.header}>
        <button
          type="button"
          onClick={onBack}
          style={styles.back}
          aria-label="Back"
        >
          ‹
        </button>
        <strong style={styles.headerTitle}>Notifications</strong>
        <span style={styles.headerSpacer} />
      </div>

      <div className="vm-scroll with-topbar">
        <section style={styles.section}>
          {error && (
            <div style={styles.error}>
              {error}
            </div>
          )}

          {loading || !preferences ? (
            <div className="vm-empty">
              Loading notification settings...
            </div>
          ) : (
            <>
              <div style={styles.card}>
                <div style={styles.row}>
                  <div style={styles.copy}>
                    <strong style={styles.title}>
                      Push notifications
                    </strong>
                    <span style={styles.detail}>
                      {permissionLabel(permission)}
                    </span>
                  </div>

                  <Toggle
                    checked={preferences.push_enabled === true}
                    disabled={savingKey === 'push_enabled'}
                    onChange={changeMaster}
                  />
                </div>
              </div>

              <div style={styles.label}>
                Notification types
              </div>

              <div style={styles.card}>
                {NOTIFICATION_TYPES.map((item, index) => (
                  <div
                    key={item.key}
                    style={{
                      ...styles.row,
                      ...(index > 0 ? styles.rowBorder : {})
                    }}
                  >
                    <div style={styles.copy}>
                      <strong style={styles.title}>
                        {item.label}
                      </strong>
                    </div>

                    <Toggle
                      checked={preferences[item.key] !== false}
                      disabled={
                        savingKey === item.key ||
                        preferences.push_enabled === false
                      }
                      onChange={enabled =>
                        changeType(item.key, enabled)
                      }
                    />
                  </div>
                ))}
              </div>

              {permission === 'denied' && (
                <p style={styles.note}>
                  Android has the final say over whether this app may display
                  notifications. If notifications are blocked there, the
                  switches above cannot override Android.
                </p>
              )}
            </>
          )}
        </section>
      </div>
    </>
  );
}

const styles = {
  header: {
    position: 'absolute',
    inset: '0 0 auto 0',
    height: '52px',
    display: 'grid',
    gridTemplateColumns: '48px 1fr 48px',
    alignItems: 'center',
    background: '#fff',
    borderBottom: '1px solid #e2e8ee',
    zIndex: 10
  },
  back: {
    border: 0,
    background: 'transparent',
    color: '#215b7d',
    fontSize: '31px',
    lineHeight: 1,
    padding: 0,
    cursor: 'pointer'
  },
  headerTitle: {
    textAlign: 'center',
    color: '#1f2933',
    fontSize: '16px'
  },
  headerSpacer: {
    display: 'block'
  },
  section: {
    padding: '16px'
  },
  label: {
    margin: '18px 4px 8px',
    color: '#75889a',
    fontSize: '11px',
    fontWeight: 800,
    letterSpacing: '0.08em',
    textTransform: 'uppercase'
  },
  card: {
    overflow: 'hidden',
    background: '#fff',
    border: '1px solid #e0e7ed',
    borderRadius: '12px'
  },
  row: {
    minHeight: '66px',
    padding: '12px 14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '14px'
  },
  rowBorder: {
    borderTop: '1px solid #edf1f4'
  },
  copy: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '3px'
  },
  title: {
    color: '#243b53',
    fontSize: '14px'
  },
  detail: {
    color: '#829ab1',
    fontSize: '11px',
    lineHeight: 1.35
  },
  switch: {
    flexShrink: 0,
    display: 'inline-flex',
    cursor: 'pointer'
  },
  hiddenCheckbox: {
    position: 'absolute',
    opacity: 0,
    pointerEvents: 'none'
  },
  track: {
    width: '44px',
    height: '25px',
    padding: '2px',
    boxSizing: 'border-box',
    borderRadius: '999px',
    background: '#cbd5df',
    transition: 'background 120ms ease'
  },
  trackOn: {
    background: '#215b7d'
  },
  trackDisabled: {
    opacity: 0.45
  },
  thumb: {
    display: 'block',
    width: '21px',
    height: '21px',
    borderRadius: '50%',
    background: '#fff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
    transform: 'translateX(0)',
    transition: 'transform 120ms ease'
  },
  thumbOn: {
    transform: 'translateX(19px)'
  },
  note: {
    margin: '13px 4px 0',
    color: '#75889a',
    fontSize: '11px',
    lineHeight: 1.45
  },
  error: {
    marginBottom: '12px',
    border: '1px solid #f0c8c5',
    borderRadius: '8px',
    background: '#fff1f0',
    color: '#a33',
    padding: '10px 12px',
    fontSize: '12px',
    lineHeight: 1.4
  }
};
